import { motion } from "framer-motion";
import { Tag, ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { format, parse, addMonths, subMonths } from "date-fns";
import { ptBR } from "date-fns/locale";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

// FIN-5 — HeroCategoriasCard.
// Hero purple com total gasto + month selector + footer (top categoria,
// counts).

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

function _refMonthDate(refMonth) {
  try {
    return parse(`${refMonth}-01`, "yyyy-MM-dd", new Date());
  } catch {
    return new Date();
  }
}

function _toIsoMonth(d) { return format(d, "yyyy-MM"); }

export default function HeroCategoriasCard({
  breakdown,
  month,
  onChangeMonth,
  loading,
}) {
  const totalSpent = Number(breakdown?.total_spent ?? 0);
  const topCategory = breakdown?.top_category;
  const categoriesCount = Number(breakdown?.categories_count ?? 0);
  const transactionsCount = Number(breakdown?.transactions_count ?? 0);

  const cur = _refMonthDate(month);
  const today = new Date();
  const isCurrentMonth = format(cur, "yyyy-MM") === format(today, "yyyy-MM");
  const monthLabel = format(cur, "MMMM 'de' yyyy", { locale: ptBR });

  const prev = () => onChangeMonth(_toIsoMonth(subMonths(cur, 1)));
  const next = () => onChangeMonth(_toIsoMonth(addMonths(cur, 1)));

  return (
    <Card
      variant="elevated"
      className="glass-card-strong gradient-border-cyan glow-hover p-6 md:p-8 relative overflow-hidden"
      data-testid="cat-hero"
    >
      {/* Top row */}
      <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-brand-purple/15 border border-brand-purple/30 flex items-center justify-center shrink-0">
            <Tag className="w-5 h-5 text-brand-purple" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h2 className="font-display font-bold text-foreground text-h2 leading-tight" data-testid="cat-hero-title">
              Categorias de despesa
            </h2>
            <p className="text-caption text-foreground-muted">Total gasto</p>
          </div>
        </div>

        {/* Month selector */}
        <div className="flex items-center gap-1.5 bg-surface-card-hover/40 border border-subtle rounded-lg px-1 py-0.5">
          <button
            type="button"
            onClick={prev}
            className="p-1.5 rounded-md text-foreground-muted hover:bg-surface-card-hover hover:text-foreground transition-colors"
            aria-label="Mês anterior"
            data-testid="cat-month-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div
            className="inline-flex items-center gap-2 px-2 text-caption font-medium capitalize text-foreground"
            aria-live="polite"
            data-testid="cat-month-label"
          >
            <Calendar className="w-3.5 h-3.5 text-brand-purple" aria-hidden="true" />
            {monthLabel}
          </div>
          <button
            type="button"
            onClick={next}
            disabled={isCurrentMonth}
            className={cn(
              "p-1.5 rounded-md text-foreground-muted hover:bg-surface-card-hover hover:text-foreground transition-colors",
              isCurrentMonth && "opacity-50 cursor-not-allowed",
            )}
            aria-label="Próximo mês"
            data-testid="cat-month-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Heroic amount */}
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
        className="mb-3"
        data-testid="cat-hero-amount"
      >
        <div
          className={cn(
            "font-display font-bold leading-[0.95] tracking-[-0.02em] tabular-nums",
            "text-[44px] md:text-[64px] lg:text-[80px]",
            "text-brand-purple",
          )}
        >
          {loading ? "…" : fmtBRL(totalSpent)}
        </div>
      </motion.div>

      <p className="text-caption text-foreground-muted" data-testid="cat-hero-footer">
        {topCategory ? `Top: ${topCategory} · ` : ""}
        {categoriesCount} categoria{categoriesCount === 1 ? "" : "s"}
        {" · "}
        {transactionsCount} compra{transactionsCount === 1 ? "" : "s"}
      </p>
    </Card>
  );
}
